// The gum-intent api, as a wallet frontend uses it (docs/builders-ui.md): quote → build →
// (submit) → status.

import type { JupnetBuilt } from "./jupnet";
import { readSse } from "./sse";

export type QuoteHead = {
  quote_id: string;
  src_chain_id: number;
  dst_chain_id: number;
  gross: string;
  net: string;
  builder_fee: string;
  protocol_fee: string;
  builder_fee_bps: number;
  protocol_fee_bps: number;
  validity_seconds: number;
  solvers_asked: number;
  /** the band the open accepts: the quoted gross alone, or a hosted swap's `[min_out − headroom, expected + slack]` */
  min_gross?: string;
  max_gross?: string;
  /** a hosted swap of a non-stable input into the stable (builders-ui.md §4) */
  source_swap?: SourceSwap;
  delivery?: Delivery;
  /** a Jupnet USD basket: its stablecoins (`gross`, the fees and `net` are then 6-decimal USD) */
  inputs?: BasketInput[];
};
/** One stablecoin of a basket as the api echoes it: `usd` is its value at par, 6 decimals. */
export type BasketInput = { token: string; token_wire?: string; amount: string; decimals: number; usd: string };
/** `gross` above is `expected_out`; the open's band is `[min_out, expected_out + slack]`. */
export type SourceSwap = { aggregator: string; input_token: string; input_amount: string; stable: string; expected_out: string; min_out: string; slippage_bps: number };
/** One solver's quote. `min_dst_amount` is the floor at the quoted gross; `min_dst_at_min_gross`
 *  the floor at the band's lower edge, i.e. what a hosted swap's open is guaranteed to carry. */
export type Quote = { quote_id: string; solver: string; dst_amount: string; slippage_bps: number; min_dst_amount: string; min_dst_at_min_gross?: string; valid_for_ms: number; receivedAt: number };
/** `direct`: the fill pays `dst_recipient`'s wallet. `gum_deposit`: the fill deposits into gum's
 *  bank on the destination chain and the output is credited to the Jupnet account `dst_recipient`. */
export type Delivery = "direct" | "gum_deposit";
/** One input (`input_token` + `input_amount`), or a Jupnet basket (`inputs`, 2 to 6 stablecoins). */
export type QuoteReq = { src_chain: string; dst_chain: string; input_token?: string; input_amount?: string; inputs?: { token: string; amount: string }[]; dst_token: string; dst_recipient: string; src_slippage_bps?: number; delivery?: Delivery };

export type UnsignedTx =
  | { vm: "evm"; chain_id: number; to: `0x${string}`; data: `0x${string}`; value: string }
  // a hosted swap on Solana adds `txs` (v0, one or a two-transaction Jito bundle); `tx_base64`
  // is then present only when it is one transaction
  | { vm: "solana"; tx_base64?: string; txs?: string[]; bundle?: boolean; recent_blockhash: string; last_valid_block_height: number }
  | { vm: "sui"; tx_bytes_base64: string }
  // the user's signature goes in its slot; /submit adds the api's (fee payer and `opener`)
  | JupnetBuilt;
type EvmTx = Extract<UnsignedTx, { vm: "evm" }>;
/** The approval of the swap router a hosted EVM swap still needs (none for ETH or enough allowance). */
export type Approval = { token: string; spender: string; amount: string; tx: EvmTx };
/** A gasless alternative to `approval`: sign `typed_data`, post it back to `/build` as `permit`. */
export type PermitOffer = { kind: "eip2612" | "permit2"; deadline: number; nonce?: string; typed_data: any };
export type PostedPermit = { kind: "eip2612"; deadline: number; signature: string } | { kind: "permit2"; deadline: number; nonce: string; signature: string };
export type Built = {
  quote_id: string;
  src_chain_id: number;
  dst_chain_id: number;
  min_dst_amount: string;
  args: any;
  tx: UnsignedTx;
  // hosted swap only
  source_swap?: { aggregator: string; expected_out: string; min_out: string };
  approval?: Approval | null;
  permit?: PermitOffer | null;
  router_call?: any;
};

export type IntentRow = {
  intent_hash: string;
  state: string;
  src_chain_id: number;
  dst_chain_id: number;
  escrow_user: string;
  escrow_id: number;
  input_token: string;
  input_amount: string;
  dst_token: string;
  min_dst_amount: string;
  dst_recipient: string;
  builder_fee?: string;
  protocol_fee?: string;
  /** the quote round this open was built from, and its expected amount (linked by the api) */
  quote_id?: string | null;
  quoted_dst_amount?: string | null;
  expiry_time: number | null;
  open_tx: string | null;
  fulfill_tx: string | null;
  fulfill_amount: string | null;
  settle_tx: string | null;
  void_tx: string | null;
  refund_tx: string | null;
  /** 0 direct, 1 gum_deposit (absent from an api that predates it: direct) */
  delivery?: number;
  /** gum_deposit: the fill's deposit into the bank, and the Jupnet tx that credited it (null until then) */
  deposit_tx?: string | null;
  credit_tx?: string | null;
  /** a Jupnet basket's stablecoins, gross in each one's units (null for a single input) */
  input_components?: { token: string; amount: string }[] | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
};
export type IntentEvent = { state: string; evidence: string; tx: string | null; at: string };
/** `GET /intents/{hash}`'s `quote`: the build the open came from (null if built elsewhere). */
export type IntentQuote = {
  quote_id: string;
  solver: string;
  dst_amount: string;
  slippage_bps: number;
  min_dst_amount_quoted: string;
  rate_num: string;
  rate_den: string;
  expected_at_actual: string | null;
  source_swap: SourceSwap | null;
};

async function failText(r: Response): Promise<string> {
  return (await failure(r, "")).reason;
}

/** An api refusal: its status and, for an expected one, its stable `code` (`price_moved`,
 *  `bundle_not_landed`, `broadcast_rejected`, …: docs/builders-ui.md). */
export class ApiFailure extends Error {
  constructor(
    readonly what: string,
    readonly status: number,
    readonly code: string | null,
    readonly reason: string,
  ) {
    super(what ? `${what}: ${reason}` : reason);
  }
}

async function failure(r: Response, what: string): Promise<ApiFailure> {
  const t = await r.text();
  let j: any = null;
  try {
    j = JSON.parse(t);
  } catch {}
  return new ApiFailure(what, r.status, typeof j?.code === "string" ? j.code : null, j?.error ?? t);
}

/** The swap's price moved between the quote and the build: quote again (nothing was signed). */
export const isPriceMoved = (e: unknown): boolean => e instanceof ApiFailure && e.code === "price_moved";

/** A solver's answer of no quote, with the reason it chose to give (`done.declined`). */
export type Declined = { solver: string; reason: string };

/** One quote round. Streams: `onHead` once, `onQuote` per solver; resolves when the api closes it,
 *  with the solvers that declined and why (empty from an api that does not send them). */
export async function quoteRound(
  api: string,
  req: QuoteReq,
  h: { onHead?: (h: QuoteHead) => void; onQuote?: (q: Quote) => void; signal?: AbortSignal } = {},
): Promise<{ head: QuoteHead; quotes: Quote[]; declined: Declined[] }> {
  const r = await fetch(`${api}/quote`, { method: "POST", headers: { "content-type": "application/json", accept: "text/event-stream" }, body: JSON.stringify(req), signal: h.signal });
  if (!r.ok) throw new Error(`quote: ${await failText(r)}`);
  let head: QuoteHead | undefined;
  const quotes: Quote[] = [];
  let declined: Declined[] = [];
  await readSse(r, (f) => {
    if (f.event === "request") {
      head = JSON.parse(f.data);
      h.onHead?.(head!);
    } else if (f.event === "quote") {
      const q = { ...JSON.parse(f.data), receivedAt: Date.now() } as Quote;
      quotes.push(q);
      h.onQuote?.(q);
    } else if (f.event === "done") {
      declined = declinedOf(f.data);
    }
  });
  if (!head) throw new Error("quote: the api sent no request frame");
  return { head, quotes, declined };
}

/** `done.declined`, keeping only well-formed entries. */
export function declinedOf(data: string): Declined[] {
  try {
    const d = JSON.parse(data)?.declined;
    return Array.isArray(d) ? d.filter((x) => typeof x?.solver === "string" && typeof x?.reason === "string") : [];
  } catch {
    return [];
  }
}

/** `GET /jupnet`: the destination chains with gum's bank, where a fill deposits for a Jupnet
 *  account (any token: the relayer registers a missing Jupnet mint with the first credit), and
 *  `source`: gum-intent's Jupnet program and the stablecoins it escrows (null until deployed). */
export type JupnetChains = {
  gum_omnichain: string;
  chains: { chain: string; chain_id: number; vm: string; bank: string }[];
  source?: { chain: string; chain_id: number; program: string; stables: string[]; basket?: { token: string; max_components: number } } | null;
};

export async function jupnetChains(api: string): Promise<JupnetChains> {
  const r = await fetch(`${api}/jupnet`);
  if (!r.ok) throw new Error(`jupnet: ${await failText(r)}`);
  return r.json();
}

/** The quote that delivers the most; ties go to the earlier one. */
export function pickBest(quotes: Quote[]): Quote | undefined {
  return quotes.reduce<Quote | undefined>((best, q) => (!best || BigInt(q.dst_amount) > BigInt(best.dst_amount) ? q : best), undefined);
}

/** Whether a quote can still be built (the api refuses it after its validity window). */
export function quoteLive(q: Quote, now = Date.now()): boolean {
  return now < q.receivedAt + q.valid_for_ms;
}

export type BuildReq = { quote_id: string; solver: string; dst_amount: string; slippage_bps?: number; user: string; permit?: PostedPermit };

/** Quote rounds: `start()` tags a new round and returns whether it is still current; `retire()`
 *  (an input changed) makes every earlier round stale, so its late frames are dropped. */
export function quoteRounds() {
  let n = 0;
  return {
    start(): () => boolean {
      const mine = ++n;
      return () => n === mine;
    },
    retire() {
      n++;
    },
  };
}

export async function build(api: string, b: BuildReq): Promise<Built> {
  const r = await fetch(`${api}/build`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(b) });
  if (!r.ok) throw await failure(r, "build");
  return r.json();
}

/** The broadcast transaction id, and the open's `intent_hash` when the api saw the open within
 *  its short wait (null otherwise: register it, or read it from the logs). `signed`: one
 *  transaction, or a Solana hosted swap's bundle (the api lands it via Jito). */
export async function submit(api: string, src_chain: string, signed: string | string[]): Promise<{ tx: string; intentHash: string | null }> {
  const r = await fetch(`${api}/submit`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ src_chain, signed }) });
  if (!r.ok) throw await failure(r, "submit");
  const j = (await r.json()) as any;
  return { tx: j.tx, intentHash: j.intent_hash ?? null };
}

/** What `POST /intents` said about a transaction. */
export type Registered = { kind: "indexed"; intentHash: string } | { kind: "pending" } | { kind: "refused"; reason: string };

export async function registerOpen(api: string, src_chain: string, tx: string, f: typeof fetch = fetch): Promise<Registered> {
  const r = await f(`${api}/intents`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ src_chain, tx }) });
  if (r.status === 200 || r.status === 201) return { kind: "indexed", intentHash: ((await r.json()) as any).intent_hash };
  if (r.status === 202) return { kind: "pending" };
  return { kind: "refused", reason: await failText(r) };
}

/** Register an open until the api has indexed it (its transaction lands in seconds): the
 *  `intent_hash`, or null if the api refuses it or it is still not visible after the last wait. */
export async function awaitRegistered(api: string, src_chain: string, tx: string, waits = [300, 500, 800, 1200, 2000, 3000, 5000, 8000], f: typeof fetch = fetch): Promise<string | null> {
  for (const ms of [0, ...waits]) {
    if (ms) await new Promise((r) => setTimeout(r, ms));
    const got = await registerOpen(api, src_chain, tx, f).catch(() => ({ kind: "pending" }) as Registered);
    if (got.kind === "indexed") return got.intentHash;
    if (got.kind === "refused") return null;
  }
  return null;
}

/** The first of `ps` to resolve to a value; null when every one resolves null (or fails). */
export function firstValue<T>(ps: Promise<T | null | undefined>[]): Promise<T | null> {
  return new Promise((resolve) => {
    let left = ps.length;
    if (!left) resolve(null);
    for (const p of ps)
      p.then(
        (v) => (v != null ? resolve(v) : --left === 0 && resolve(null)),
        () => --left === 0 && resolve(null),
      );
  });
}

/** `{ intent, events }`, or null while the indexer has not seen the open yet. */
export async function getIntent(api: string, hash: string): Promise<{ intent: IntentRow; events: IntentEvent[]; quote?: IntentQuote | null } | null> {
  const r = await fetch(`${api}/intents/${hash}`);
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`intent: ${await failText(r)}`);
  return r.json();
}

/** A wallet's intents, newest first: on `srcChain`, or (none given) on every chain. */
export async function intentsOf(api: string, user: string, srcChain?: string, limit = 20, f: typeof fetch = fetch): Promise<IntentRow[]> {
  const chain = srcChain ? `&src_chain=${encodeURIComponent(srcChain)}` : "";
  const r = await f(`${api}/intents?escrow_user=${encodeURIComponent(user)}${chain}&limit=${limit}`);
  if (!r.ok) throw new Error(`intents: ${await failText(r)}`);
  return ((await r.json()) as any).intents ?? [];
}
